import { ServerSideDataGridModel } from "src/components/common/dataGrid/gridModel/ServerSideDataGridModel.tsx";
import {
	AddShopOrdersToBatchDataGridApi,
	AddShopOrdersToBatchDataGridApi_AttributeFilter,
} from "src/api/generated/erp/production/shopOrderBatch/api/addShopOrdersToBatchDataGridApi.ts";
import {
	dateColumn,
	dateTimeColumn,
	enumColumn,
	floatColumn,
	integerColumn,
	textColumn,
} from "src/components/common/dataGrid/columns.tsx";
import i18n from "i18next";
import { getShopOrderSourceTypeLabels } from "src/api/generated/erp/db/types/enums/shopOrderSourceType.ts";
import { OpenShopOrderButton } from "src/components/views/erp/utilComponents/OpenShopOrderButton.tsx";
import { OpenCustomerOrderButton } from "src/components/views/erp/utilComponents/OpenCustomerOrderButton.tsx";
import { AddShopOrdersToBatchDataGridApi_ShopOrderDto } from "src/api/generated/erp/production/shopOrderBatch/api/addShopOrdersToBatchDataGridApi.ts";
import { AavoTextField } from "src/components/common/inputFields/AavoTextField.tsx";
import { AavoSelectField } from "../../../../common/inputFields/AavoSelectField.tsx";
import { FormCrudDataGrid } from "src/components/common/dataGrid/crud/FormCrudDataGrid";
import { genericNullableValue } from "src/utils/genericNullableValue.ts";
import { ConfiguratorFieldComponentsAsFilter } from "src/components/views/erp/configurator/ConfiguratorFieldComponentsAsFilter.tsx";
import { useTenantCustomizations } from "src/tenantCustomizations/TenantCustomizationsContext.ts";
import { ConfigurationPropertyValueFilter } from "src/api/generated/erp/configurator/propertyValueFiltering/configurationPropertyValueFilter.ts";
import { OpenPartConfigurationButton } from "src/components/views/erp/configurator/inspecting/OpenPartConfigurationButton.tsx";
import { OpenSitePartButton } from "src/components/views/erp/utilComponents/OpenSitePartButton.tsx";
import React from "react";
import { useGlobalInitData } from "src/contexts/GlobalInitDataContext.ts";
import { SxProps } from "@mui/system";
import { Theme } from "@mui/material";
import { AddShopOrdersToBatchAttributeFilters } from "src/components/views/erp/production/shopOrderBatch/AddShopOrdersToBatchAttributeFilters.tsx";
import { getPartRevisionShortDescription } from "src/components/views/erp/erpUtils.ts";
import { faPaperclip } from "@fortawesome/pro-regular-svg-icons";
import { AsyncMenuButton } from "src/components/common/contextMenu/AsyncMenuButton.tsx";
import { useGenericDialog } from "src/components/common/dialogs/GenericDialogContext.ts";
import { ShopOrdersExtendedDocumentsView } from "src/components/views/erp/production/shopOrder/ShopOrderExtendedDocumentsView.tsx";
import { formatDayJs } from "../../../../../utils/dayjsUtils.ts";
import { CellWithRemarkIndicator } from "src/components/common/dataGrid/cells/CellWithRemarkIndicator.tsx";
import { defineAgentUiContextKey } from "src/components/views/ai/assistant/uiContext/agentUiContextKey.ts";

export interface AddShopOrdersToBatchDataGridBaseProps {
	defaultProductionLineId?: number | null;
	actionBarComponents?: (params: { selectedRows: AddShopOrdersToBatchDataGridApi_ShopOrderDto[] }) => React.ReactNode;
	rowContextMenuComponents?: (params: {
		row: AddShopOrdersToBatchDataGridApi_ShopOrderDto;
		allSelectedRows: AddShopOrdersToBatchDataGridApi_ShopOrderDto[];
		refreshData: () => Promise<unknown>;
	}) => React.ReactNode[];
	onSelectionChanged?: (selectedRows: AddShopOrdersToBatchDataGridApi_ShopOrderDto[]) => void;
	checkboxSelection?: boolean;
	sx?: SxProps<Theme>;
	onlyAlreadyConnectedParts?: boolean;
	shopOrderBatchId?: number;
}

export const AddShopOrdersToBatchDataGridBase = ({
	defaultProductionLineId,
	actionBarComponents,
	rowContextMenuComponents,
	onSelectionChanged,
	sx,
	checkboxSelection = false,
	onlyAlreadyConnectedParts = false,
	shopOrderBatchId,
}: AddShopOrdersToBatchDataGridBaseProps) => {
	const tenantCustomizations = useTenantCustomizations();
	const tenantConfig = tenantCustomizations.tenantConfig;
	const { defaultSiteId } = useGlobalInitData();
	const { openDialog } = useGenericDialog();
	const tenantCustomColumns = tenantCustomizations.erp?.production?.addShopOrdersToBatchView?.customColumns;

	return (
		<ServerSideDataGridModel
			gridId={"7480A5AD2D5105B4F"}
			fetchData={(props) =>
				AddShopOrdersToBatchDataGridApi.getShopOrdersToAdd({
					siteId: defaultSiteId,
					onlyAlreadyConnectedParts: onlyAlreadyConnectedParts,
					shopOrderBatchId: shopOrderBatchId,
					...props,
				})
			}
			getDataModelResult={(data) => data.data}
			getRowId={(row) => row.shopOrderId}
			initialParams={{
				searchQuery: "",
				productionLineId: genericNullableValue<number>(defaultProductionLineId),
				propertyValueFilters: Array<ConfigurationPropertyValueFilter>(),
				attributeValueFilters: Array<AddShopOrdersToBatchDataGridApi_AttributeFilter>(),
			}}
			onSelectionChanged={onSelectionChanged}
			render={({ data, currentParams, dataGridProps, refreshData, selectedRows }) => (
				<FormCrudDataGrid<AddShopOrdersToBatchDataGridApi_ShopOrderDto>
					checkboxSelection={checkboxSelection}
					sx={sx}
					columns={[
						integerColumn({
							field: "shopOrderId",
							headerName: i18n.t("number_short"),
						}),
						textColumn({
							field: "productionLineName",
							headerName: i18n.t("production_line"),
						}),
						textColumn({
							field: "sourceDescription",
							headerName: i18n.t("reference"),
						}),
						textColumn({
							field: "configurationOrPartDescription",
							headerName: i18n.t("part"),
							width: 300,
							renderCell: ({ row }) => (
								<CellWithRemarkIndicator
									value={row.configurationOrPartDescription}
									remark={[
										{ label: i18n.t("sales_remark"), value: row.partSalesRemark },
										{ label: i18n.t("acquisition_remark"), value: row.partAcquisitionRemark },
									]}
								/>
							),
						}),
						textColumn({
							field: "shopOrderNote",
							headerName: i18n.t("note"),
							width: 150,
						}),
						floatColumn({
							field: "quantity",
							headerName: i18n.t("quantity"),
							width: 70,
						}),
						textColumn({
							field: "partUnit",
							headerName: i18n.t("unit"),
							width: 70,
						}),
						textColumn({
							field: "partRevision",
							headerName: i18n.t("revision"),
							width: 70,
							valueGetter: (_, row) =>
								getPartRevisionShortDescription({
									revisionNumber: row.partRevision,
									note: row.partRevisionNote,
								}),
						}),
						floatColumn({
							field: "capacityQuantity",
							headerName: i18n.t("capacity_quantity"),
							width: 140,
						}),
						dateColumn({
							field: "demandDate",
							headerName: i18n.t("demand_date"),
							valueFormatter: (value) => formatDayJs(value, `L [(${i18n.t("week_abbr")}] w)`),
						}),
						dateTimeColumn({
							field: "creationDate",
							headerName: i18n.t("created_at"),
						}),
						textColumn({
							field: "createdBy",
							headerName: i18n.t("created_by"),
						}),
						enumColumn({
							field: "source",
							headerName: i18n.t("source"),
							enumLabels: getShopOrderSourceTypeLabels(),
							width: 150,
						}),
						...(tenantCustomColumns?.() ?? []),
					]}
					actionBarComponents={
						<>
							<AavoTextField
								label={i18n.t("search")}
								onSubmit={async (value) => {
									await refreshData({ searchQuery: value });
								}}
							/>
							<AavoSelectField
								label={i18n.t("production_line")}
								options={data.productionLineOptions}
								value={currentParams.productionLineId}
								getOptionKey={(o) => o.productionLineId}
								getOptionLabel={(o) => o.productionLineName}
								onChange={async (value) => {
									await refreshData({ productionLineId: value });
								}}
							/>
							<ConfiguratorFieldComponentsAsFilter
								components={
									tenantCustomizations.erp?.production?.addShopOrdersToBatchView
										?.configuratorFilterComponents ?? []
								}
								filterValues={currentParams.propertyValueFilters}
								onSubmit={(filters) => refreshData({ propertyValueFilters: filters })}
							/>
							<AddShopOrdersToBatchAttributeFilters
								siteId={defaultSiteId}
								productionLineId={currentParams.productionLineId}
								filterValues={currentParams.attributeValueFilters}
								onSubmit={(filters) => refreshData({ attributeValueFilters: filters })}
								onlyAlreadyConnectedParts={onlyAlreadyConnectedParts}
								shopOrderBatchId={shopOrderBatchId}
							/>
							{actionBarComponents?.({ selectedRows })}
						</>
					}
					rowContextMenuComponents={({ onlySingleRowSelected, row, allSelectedRows }) => {
						return [
							onlySingleRowSelected && (
								<OpenShopOrderButton key={"openShopOrder"} shopOrderId={row.shopOrderId} />
							),
							onlySingleRowSelected && (
								<OpenCustomerOrderButton
									key={"openCustomerOrder"}
									customerOrderId={row.customerOrderId}
								/>
							),
							onlySingleRowSelected && (
								<OpenPartConfigurationButton
									key={"openConfiguration"}
									partConfigurationId={row.partConfigurationId}
									partConfiguratorType={{
										type: "shopOrder",
										shopOrderId: row.shopOrderId,
										explicitPartConfigurationId: null,
									}}
								/>
							),
							onlySingleRowSelected && (
								<OpenSitePartButton key={"openPart"} partId={row.partId} />
							),
							onlySingleRowSelected && tenantConfig.documentsEnabled && (
								<AsyncMenuButton
									key={"documents"}
									label={i18n.t("documents")}
									icon={faPaperclip}
									onClick={async () => {
										openDialog({
											title: i18n.t("documents"),
											agentContext: {
												key: defineAgentUiContextKey("erp.production.shopOrderExtendedDocuments.dialog"),
												title: i18n.t("documents"),
												data: {
													shopOrderIds: [row.shopOrderId],
												},
											},
											content: (
												<ShopOrdersExtendedDocumentsView
													shopOrderIds={[row.shopOrderId]}
													addDocumentsTo={{ objectType: "SHOP_ORDER", objectId: row.shopOrderId }}
												/>
											),
										});
									}}
								/>
							),
							rowContextMenuComponents?.({ row, allSelectedRows, refreshData }),
						];
					}}
					agentContext={{
						key: defineAgentUiContextKey("erp.production.addShopOrdersToBatch.grid"),
						data: {
							shopOrderBatchId: shopOrderBatchId ?? null,
							productionLineId: currentParams.productionLineId ?? null,
							onlyAlreadyConnectedParts,
						},
						rowFields: ["shopOrderId", "partId", "source", "sourceId"],
						selectedRowPathSegment: (row) => `${i18n.t("shop_order")} ${row.shopOrderId}`,
					}}
					{...dataGridProps}
				/>
			)}
		/>
	);
};
